import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  AlertTriangle,
  Phone,
  Stethoscope,
  Scale,
  Building2,
  Calculator,
  Briefcase,
  ChevronRight,
} from "lucide-react";
import { industries, companyInfo } from "../data/mock";
import { useScrollReveal } from "../hooks/useScrollReveal";

const iconMap = { Stethoscope, Scale, Building2, Calculator, Briefcase };

const Section = ({ children, className = "" }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section
      ref={ref}
      className={`reveal ${isVisible ? "visible" : ""} ${className}`}
    >
      {children}
    </section>
  );
};

export default function IndustryDetail() {
  const { industryId } = useParams();
  const industry = industries.find((i) => i.id === industryId);

  if (!industry) {
    return <Navigate to="/industries" replace />;
  }

  const Icon = iconMap[industry.icon] || Briefcase;
  const otherIndustries = industries.filter((i) => i.id !== industryId);

  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-50 py-16 lg:py-20">
        <div className="container mx-auto px-6 page-enter">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
            <Link
              to="/industries"
              className="hover:text-blue-600 transition-colors"
            >
              Industries
            </Link>
            <span>/</span>
            <span className="text-slate-900 font-medium">
              {industry.title}
            </span>
          </div>
          <div className="flex items-start gap-5">
            <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
              <Icon className="w-7 h-7 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight mb-3">
                {industry.seoTitle || `IT Support for ${industry.title}`}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-3xl">
                {industry.description}
              </p>
            </div>
          </div>

          {/* Trust strip */}
          <div className="flex flex-wrap gap-4 mt-8 pt-6 border-t border-slate-200">
            {[
              "Compliance-focused approach",
              "Long-term industry experience",
              "Dedicated account manager",
            ].map((item, i) => (
              <span key={i} className="flex items-center gap-1.5 text-sm text-slate-500">
                <Check className="w-3.5 h-3.5 text-blue-600" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges + Solutions */}
      <Section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Challenges */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                IT Challenges for {industry.title}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                We understand the specific technology challenges facing{" "}
                {industry.title.toLowerCase()}. Here are the most common pain
                points we see:
              </p>
              <div className="space-y-4">
                {industry.challenges.map((challenge, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-amber-50 flex items-center justify-center shrink-0 mt-0.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                    </div>
                    <span className="text-slate-700 leading-relaxed">
                      {challenge}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Solutions */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                How FlexiTech Helps
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Our team has deep experience supporting{" "}
                {industry.title.toLowerCase()}. Here's how we solve these
                challenges:
              </p>
              <div className="space-y-4">
                {industry.solutions.map((solution, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <span className="text-slate-700 leading-relaxed">
                      {solution}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-slate-900 relative grain-overlay">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white tracking-tight mb-4">
              IT Support That Understands {industry.title}
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              Let's discuss how FlexiTech can improve your IT environment.
              Contact us for a free, industry-specific consultation.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white btn-primary h-12 px-8"
              >
                <Link to="/contact">
                  Get a Free Consultation
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="h-12 px-8 border-slate-600 text-white hover:bg-slate-800 hover:text-white"
              >
                <a href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}>
                  <Phone className="w-4 h-4 mr-2" />
                  {companyInfo.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Other Industries */}
      <Section className="py-16 lg:py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">
            Other Industries We Serve
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {otherIndustries.map((ind) => {
              const IndIcon = iconMap[ind.icon] || Briefcase;
              return (
                <Link key={ind.id} to={`/industries/${ind.id}`}>
                  <Card className="service-card h-full border-slate-200/60 bg-white group cursor-pointer hover:border-blue-100">
                    <CardContent className="p-6">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center mb-3 group-hover:bg-blue-50 transition-colors duration-300">
                        <IndIcon className="w-5 h-5 text-slate-600 group-hover:text-blue-600 transition-colors duration-300" />
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-1">
                        {ind.title}
                      </h3>
                      <span className="inline-flex items-center gap-1 text-sm text-blue-600 font-medium">
                        Learn more
                        <ChevronRight className="w-3.5 h-3.5" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
          <div className="mt-8 text-center">
            <Button
              asChild
              variant="outline"
              className="border-slate-300 text-slate-700"
            >
              <Link to="/industries">
                <ArrowLeft className="w-4 h-4 mr-1" />
                All Industries
              </Link>
            </Button>
          </div>
        </div>
      </Section>
    </div>
  );
}
